import { useCallback } from "react"

import {
  transport,
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import type { GetMetricHealthStatsResponse } from "@/types_api/backend/transport/get_metric_health_stats_response"
import type {
  ReviewProjectMemberResource,
  Role as ReviewProjectMemberRole,
} from "@/types_api/backend/transport/list_review_project_members_response"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"

import type {
  CreateHumanReviewProjectRequest,
  UpdateHumanReviewProjectRequest,
} from "../types/reviewProjectMutationRequests"
import { assertHumanReviewMetricLimit } from "../utils/humanReviewMetricLimit"
import {
  invalidateReviewAnnotationQueries,
  invalidateReviewOverviewCompletionQueries,
  invalidateReviewOverviewQueries,
} from "../utils/reviewOverviewCache"
import {
  mapReviewOverviewProjectToHumanReviewProject,
  mapReviewProjectResourceToHumanReviewProject,
} from "../utils/reviewProjectMappers"
import { useReviewDisagreementConversationRecheck } from "./reviewDisagreementStateTransport"

export {
  mapAnnotationsWithMetricOutputsResponse,
  useAllAnnotationsByOrgTransport,
  useAnnotationsWithMetricOutputsTransport,
  useFeedbackForSimulationOutputTransport,
} from "./reviewAnnotationQueriesTransport"
export {
  useCreateReviewAnnotationTransport,
  useDeleteReviewAnnotationTransport,
  useUpdateReviewAnnotationTransport,
} from "./reviewAnnotationMutationsTransport"
export {
  mapReviewDisagreementStateResponse,
  useReviewDisagreementStateTransport,
} from "./reviewDisagreementStateTransport"
export type { ReviewDisagreementProjectState } from "./reviewDisagreementStateTransport"
export {
  mapReviewOverviewProjectToHumanReviewProject,
  mapReviewProjectResourceToHumanReviewProject,
} from "../utils/reviewProjectMappers"

type NonEmptyAssignees = [string, ...string[]]
export function toNonEmptyAssignees(
  assignees: string[] | undefined,
): NonEmptyAssignees | undefined {
  if (assignees === undefined) {
    return undefined
  }

  if (assignees.length === 0) {
    throw new Error("Human review projects must have at least one assignee.")
  }

  return [assignees[0], ...assignees.slice(1)]
}

export function useHumanReviewProjectsTransport({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useTransportQuery({
    operation: transport.operations.getReviewOverview,
    version: 2,
    request: {
      include_linked_simulation_ids: false,
      include_simulation_completions: false,
      include_simulation_metadata: false,
    },
    options: {
      enabled,
      select: (data) =>
        data.projects.map(mapReviewOverviewProjectToHumanReviewProject),
    },
  })
}

export function useReviewProjectMembershipLogsTransport({
  projectId,
  enabled = true,
}: {
  projectId: string
  enabled?: boolean
}) {
  return useTransportQuery({
    operation: transport.operations.listReviewProjectMembershipLogs,
    request: { project_id: projectId },
    options: { enabled: !!projectId && enabled },
  })
}

export function useHumanReviewProjectWithSimulationIdsFetcherTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    async (projectId: string): Promise<HumanReviewProject> => {
      const response = await transportQueryUtils.fetch(
        transport.operationVersions.getReviewOverview.v2,
        {
          project_ids: [projectId],
          include_linked_simulation_ids: true,
          include_simulation_completions: false,
          include_simulation_metadata: false,
        },
      )
      const project = response.projects.find((row) => row.ulid === projectId)
      if (!project) {
        throw new Error("Project not found")
      }

      return mapReviewOverviewProjectToHumanReviewProject(project)
    },
    [transportQueryUtils],
  )
}

export function useMetricHealthStatsTransport({
  metricId,
  includeOldVersions = false,
  criteriaViewMode = "score",
  enabled = true,
}: {
  metricId?: string
  includeOldVersions?: boolean
  criteriaViewMode?: "score" | "criteria"
  enabled?: boolean
}) {
  return useTransportQuery({
    operation: transport.operations.getMetricHealthStats,
    request: {
      metric_id: metricId ?? "",
      include_old_versions: includeOldVersions,
      criteria_view_mode: criteriaViewMode,
    },
    options: {
      enabled: !!metricId && enabled,
      select: (response: GetMetricHealthStatsResponse) => ({
        conversations: response.conversations,
        annotations: response.annotations,
        agreementRate: response.agreement_rate,
        agreements: response.agreements,
        disagreements: response.disagreements,
        criteriaAgreement: response.criteria_agreement ?? null,
        humanAgreement: {
          humanAgreementRate: response.human_agreement.human_agreement_rate,
          humanAgreementTotal: response.human_agreement.human_agreement_total,
          humanAgreements: response.human_agreement.human_agreements,
          humanDisagreements: response.human_agreement.human_disagreements,
        },
      }),
    },
  })
}

export function useReviewMetricTransportInvalidation() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(() => {
    void transportQueryUtils.invalidateOperation(
      transport.operations.getAnnotationsWithMetricOutputs,
    )
    void transportQueryUtils.invalidateOperation(
      transport.operations.getMetricHealthStats,
    )
    void transportQueryUtils.invalidateOperation(
      transport.operations.getMetricAgreementHistory,
    )
  }, [transportQueryUtils])
}

export function useReviewAnnotationTransportInvalidation() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const recheckDisagreementState = useReviewDisagreementConversationRecheck()

  // Refresh server-computed completion maps after saves without refetching the org-wide overview.
  return useCallback(
    async (simulationOutputId: string, projectId?: string | null) => {
      // Annotation saves clear the reviewer's metric skip on the server.
      void transportQueryUtils.invalidateCacheTags([
        transport.cacheTags["review-annotations"],
        transport.cacheTags["review-metric-skips"],
      ])
      await Promise.all([
        recheckDisagreementState(simulationOutputId, projectId),
        invalidateReviewAnnotationQueries(queryClient),
        invalidateReviewOverviewCompletionQueries(queryClient),
      ])
    },
    [queryClient, recheckDisagreementState, transportQueryUtils],
  )
}

export function useHumanReviewProjectMutationsTransport() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  const invalidateReviewProjectQueries = useCallback(() => {
    void invalidateReviewOverviewQueries(queryClient)
    void invalidateHomeQueries(queryClient)
    void transportQueryUtils.invalidate(transport.resources["review-projects"])
    void transportQueryUtils.invalidate(
      transport.resources["review-annotations"],
    )
    void transportQueryUtils.invalidateOperation(
      transport.operations.getReviewProject,
    )
  }, [queryClient, transportQueryUtils])

  const createMutation = useTransportMutation(
    transport.operations.createReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
      onError: invalidateReviewProjectQueries,
    },
  )
  const updateMutation = useTransportMutation(
    transport.operations.updateReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
      onError: invalidateReviewProjectQueries,
    },
  )
  const deleteMutation = useTransportMutation(
    transport.operations.deleteReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )
  const duplicateMutation = useTransportMutation(
    transport.operations.duplicateReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )

  return {
    createProject: async (request: CreateHumanReviewProjectRequest) => {
      const linkedSimulationIds = request.simulationOutputs ?? []
      const linkedMetricIds = request.metrics ?? []

      assertHumanReviewMetricLimit(request.metrics)

      return mapReviewProjectResourceToHumanReviewProject(
        (
          await createMutation.mutateAsync({
            display_name: request.name,
            description: request.description,
            assignees: request.assignees,
            linked_simulation_ids: linkedSimulationIds,
            linked_metric_ids: linkedMetricIds,
            notifications: request.notifications,
            project_type: request.projectType,
            project_rules: request.projectRules,
            review_label_options: request.reviewLabelOptions,
            ...(request.reviewLabelEnabled !== undefined && {
              review_label_enabled: request.reviewLabelEnabled,
            }),
            review_label_input_mode: request.reviewLabelInputMode,
            review_label_selection_mode: request.reviewLabelSelectionMode,
            ...(request.enforcedCollaboration !== undefined
              ? { enforced_collaboration: request.enforcedCollaboration }
              : {}),
            ...(request.allowMetricSkipping !== undefined
              ? { allow_metric_skipping: request.allowMetricSkipping }
              : {}),
            ...(request.skippableMetricIds !== undefined
              ? { skippable_metric_ids: request.skippableMetricIds }
              : {}),
            ...(request.batchAssignmentMode !== undefined
              ? { batch_assignment_mode: request.batchAssignmentMode }
              : {}),
            ...(request.batchSize !== undefined
              ? { batch_size: request.batchSize }
              : {}),
          })
        ).review_project,
      )
    },
    updateProject: async (request: UpdateHumanReviewProjectRequest) => {
      assertHumanReviewMetricLimit(request.metrics)
      return mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            display_name: request.name,
            description: request.description,
            assignees: toNonEmptyAssignees(request.assignees),
            ...(request.addAssignees?.length
              ? { add_assignees: request.addAssignees }
              : {}),
            ...(request.removeAssignees?.length
              ? { remove_assignees: request.removeAssignees }
              : {}),
            linked_simulation_ids: request.simulationOutputs,
            linked_metric_ids: request.metrics,
            notifications: request.notifications,
            project_type: request.projectType,
            project_rules: request.projectRules,
            ...(request.blindLabelingShownMetricIds !== undefined
              ? {
                  blind_labeling_shown_metric_ids:
                    request.blindLabelingShownMetricIds,
                }
              : {}),
            opted_out_assignees: request.optedOutAssignees,
            review_label_options: request.reviewLabelOptions,
            ...(request.reviewLabelEnabled !== undefined && {
              review_label_enabled: request.reviewLabelEnabled,
            }),
            review_label_input_mode: request.reviewLabelInputMode,
            review_label_selection_mode: request.reviewLabelSelectionMode,
            ...(request.enforcedCollaboration !== undefined
              ? { enforced_collaboration: request.enforcedCollaboration }
              : {}),
            ...(request.metricAdditionCompletionAction !== undefined
              ? {
                  metric_addition_completion_action:
                    request.metricAdditionCompletionAction,
                }
              : {}),
            ...(request.allowMetricSkipping !== undefined
              ? { allow_metric_skipping: request.allowMetricSkipping }
              : {}),
            ...(request.skippableMetricIds !== undefined
              ? { skippable_metric_ids: request.skippableMetricIds }
              : {}),
            ...(request.batchAssignmentMode !== undefined
              ? { batch_assignment_mode: request.batchAssignmentMode }
              : {}),
            ...(request.batchSize !== undefined
              ? { batch_size: request.batchSize }
              : {}),
            ...(request.progressIncludedAssignees !== undefined
              ? {
                  progress_included_assignees:
                    request.progressIncludedAssignees,
                }
              : {}),
            ...(request.progressExcludedAssignees !== undefined
              ? {
                  progress_excluded_assignees:
                    request.progressExcludedAssignees,
                }
              : {}),
          })
        ).review_project,
      )
    },
    // Membership deltas cannot include other fields and avoid overwriting concurrent edits.
    addSimulationOutputs: async (request: {
      id: string
      simulationOutputs: string[]
    }) =>
      mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            add_linked_simulation_ids: request.simulationOutputs,
          })
        ).review_project,
      ),
    removeSimulationOutputs: async (request: {
      id: string
      simulationOutputs: string[]
    }) =>
      mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            remove_linked_simulation_ids: request.simulationOutputs,
          })
        ).review_project,
      ),
    deleteProject: (projectId: string) =>
      deleteMutation.mutateAsync({ project_id: projectId }),
    duplicateProject: async (projectId: string) =>
      mapReviewProjectResourceToHumanReviewProject(
        (await duplicateMutation.mutateAsync({ project_id: projectId }))
          .review_project,
      ),
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    duplicateError: duplicateMutation.error,
  }
}

export type { ReviewProjectMemberRole }
// Effective project roles; MANAGER wins duplicates and absent rows are annotators.
export function useReviewProjectMembersTransport(
  projectId: string | undefined,
  {
    enabled = true,
    fresh = false,
  }: { enabled?: boolean; fresh?: boolean } = {},
) {
  return useTransportQuery({
    operation: transport.operations.listReviewProjectMembers,
    request: { project_id: projectId ?? "" },
    options: {
      enabled: enabled && Boolean(projectId),
      select: (data) => data.members,
      ...(fresh ? { refetchOnMount: "always" as const, staleTime: 0 } : {}),
    },
  })
}

export function useReviewProjectMemberMutationsTransport() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  const setRoleMutation = useTransportMutation(
    transport.operations.setReviewProjectMemberRole,
    {
      onSuccess: () => {
        void transportQueryUtils.invalidate(
          transport.resources["review-project-members"],
        )
        void transportQueryUtils.invalidateCacheTags([
          transport.cacheTags["review-projects"],
        ])
        void invalidateReviewOverviewQueries(queryClient)
      },
    },
  )

  return {
    setMemberRole: async (request: {
      projectId: string
      assignee: string
      role: ReviewProjectMemberRole
    }): Promise<ReviewProjectMemberResource> =>
      (
        await setRoleMutation.mutateAsync({
          project_id: request.projectId,
          assignee: request.assignee,
          role: request.role,
        })
      ).member,
    isSettingRole: setRoleMutation.isPending,
    setRoleError: setRoleMutation.error,
  }
}
